export const PORTAL_TIME_ZONE = "America/Los_Angeles";

// Compare calendar days, not elapsed hours, so Yesterday survives DST changes.
function portalCalendarDay(date: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: PORTAL_TIME_ZONE, year: "numeric", month: "numeric", day: "numeric",
  }).formatToParts(date);
  const part = (type: string) => Number(parts.find((entry) => entry.type === type)?.value);
  return Date.UTC(part("year"), part("month") - 1, part("day"));
}

export function formatPortalActivityDateTime(
  value?: string | null,
  now: Date = new Date(),
): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const daysAgo = (portalCalendarDay(now) - portalCalendarDay(date)) / 86400000;
  if (daysAgo === 0 || daysAgo === 1) {
    const time = new Intl.DateTimeFormat("en-US", {
      timeZone: PORTAL_TIME_ZONE, hour: "numeric", minute: "2-digit", timeZoneName: "short",
    }).format(date);
    return `${daysAgo === 0 ? "Today" : "Yesterday"}, ${time}`;
  }
  return formatPortalDateTime(value);
}

export function formatPortalDateTime(
  value?: string | null,
  options: Intl.DateTimeFormatOptions = {},
): string {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
    ...options,
    timeZone: PORTAL_TIME_ZONE,
  }).format(date);
}

export function formatLocalDateTime(value?: string | null): string {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export function formatPortalDate(value?: string | null): string {
  if (!value) return "—";

  // Date-only database fields are calendar dates, not UTC timestamps.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());

  if (dateOnly) {
    const [, year, month, day] = dateOnly;
    const yearNumber = Number(year);
    const monthNumber = Number(month);
    const dayNumber = Number(day);
    const date = new Date(Date.UTC(yearNumber, monthNumber - 1, dayNumber));

    if (
      date.getUTCFullYear() !== yearNumber ||
      date.getUTCMonth() !== monthNumber - 1 ||
      date.getUTCDate() !== dayNumber
    ) {
      return "—";
    }

    return new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-US", {
    timeZone: PORTAL_TIME_ZONE,
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}
